import {
    Backdrop,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import parse, { Element, HTMLReactParserOptions } from "html-react-parser";
import { config } from "JS/Config";
import { messaging } from "JS/Helpers/UserMessaging";
import { useIncomeDisclosureGate } from "JS/React/Hooks/IncomeDisclosure";
import { AppButton } from "JS/React/Components/AppButton";
import { AppTypography } from "JS/React/Components/AppTypography";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";

const aboveOtherDialogs = (theme: Theme) => theme.zIndex.modal + 1;
const aboveGate = (theme: Theme) => theme.zIndex.modal + 2;

const parseOptions: HTMLReactParserOptions = {
    replace(domNode) {
        if (domNode instanceof Element && domNode.name === "a") {
            domNode.attribs.target = "_blank";
            domNode.attribs.rel = "noopener noreferrer";
        }
    },
};

const StyledLoaderBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: "200px",
    padding: "20px",
    background: theme.palette.background.default,
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    color: theme.palette.common.black,
}));

const StyledBody = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.common.black,
    "& p": {
        margin: 0,
    },
    "& a": {
        color: theme.palette.primary.main,
    },
}));

const StyledErrorText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2),
    gap: theme.spacing(1),
}));

const StyledDeclineButton = styled(AppButton)(({ theme }) => ({
    background: theme.palette.grey[300],
    color: theme.palette.grey[900],
    "&:hover": {
        background: theme.palette.grey[400],
        color: theme.palette.grey[900],
    },
}));

export const IncomeDisclosureGate = () => {
    const {
        status,
        disclosure,
        accepting,
        acceptError,
        declining,
        offlineNotice,
        accept,
        decline,
        retry,
        dismissOfflineNotice,
    } = useIncomeDisclosureGate(config.user.memberId);

    if (status === "hidden") return null;

    if (status === "checking") {
        return (
            <Backdrop
                open={true}
                sx={{ zIndex: aboveOtherDialogs, backdropFilter: "blur(4px)" }}
            >
                <StyledLoaderBox>
                    <AppCircularProgress />
                    <StyledBody>{messaging.incomeDisclosure.loading}</StyledBody>
                </StyledLoaderBox>
            </Backdrop>
        );
    }

    const isLoadError = status === "loadError";
    const busy = accepting || declining;

    return (
        <Dialog
            open={true}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            sx={{ zIndex: aboveOtherDialogs }}
            slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
        >
            <StyledDialogTitle>
                {isLoadError
                    ? messaging.incomeDisclosure.heading
                    : disclosure.en.heading}
            </StyledDialogTitle>
            <DialogContent dividers>
                {isLoadError ? (
                    <StyledBody>{messaging.incomeDisclosure.loadError}</StyledBody>
                ) : (
                    <StyledBody component="div">
                        {parse(disclosure.en.body, parseOptions)}
                    </StyledBody>
                )}
                {acceptError && (
                    <StyledErrorText>
                        {messaging.incomeDisclosure.acceptError}
                    </StyledErrorText>
                )}
            </DialogContent>
            <StyledDialogActions>
                {isLoadError ? (
                    <AppButton buttonVariant="primary-filled" onClick={retry}>
                        {messaging.incomeDisclosure.retry}
                    </AppButton>
                ) : (
                    <>
                        <AppButton
                            buttonVariant="primary-filled"
                            disabled={busy}
                            onClick={accept}
                        >
                            {messaging.incomeDisclosure.accept}
                        </AppButton>
                        <StyledDeclineButton disabled={busy} onClick={decline}>
                            {messaging.incomeDisclosure.decline}
                        </StyledDeclineButton>
                    </>
                )}
            </StyledDialogActions>
            <Dialog
                open={offlineNotice}
                onClose={dismissOfflineNotice}
                maxWidth="xs"
                sx={{ zIndex: aboveGate }}
            >
                <DialogContent>
                    <StyledBody>{messaging.incomeDisclosure.offlineAction}</StyledBody>
                </DialogContent>
                <StyledDialogActions>
                    <AppButton
                        buttonVariant="primary-filled"
                        onClick={dismissOfflineNotice}
                    >
                        {messaging.incomeDisclosure.ok}
                    </AppButton>
                </StyledDialogActions>
            </Dialog>
        </Dialog>
    );
};
